if (!customElements.get('pastael-community-slider')) {
  class PastaelCommunitySlider extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-slider-viewport]');
      this.track = this.querySelector('[data-slider-track]');
      this.dots = Array.from(this.querySelectorAll('[data-slider-dot]'));
      this.prevButton = this.querySelector('[data-slider-prev]');
      this.nextButton = this.querySelector('[data-slider-next]');

      if (!this.viewport || !this.track) return;

      this.originalSlides = Array.from(this.track.querySelectorAll('[data-slider-slide]:not([data-slider-clone])'));
      if (!this.originalSlides.length) return;

      this.slideItems = [];
      this.index = Math.max(
        0,
        this.originalSlides.findIndex((slide) => slide.classList.contains('is-active'))
      );
      this.prependCount = 0;
      this.appendCount = 0;
      this.trackMode = '';
      this.inactiveSlideWidth = 0;
      this.activeSlideWidth = 0;
      this.trackGap = 0;
      this.trackPaddingStart = 0;
      this.mediaQuery = window.matchMedia('(min-width: 750px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleTrackClick = this.handleTrackClick.bind(this);
      this.isScrollingProgrammatically = false;
      this.isLooping = false;
      this.isAnimating = false;
      this.autoplayPaused = false;
      this.easing = 'cubic-bezier(0.22, 1, 0.36, 1)';
      this.transitionMs = this.reducedMotion ? 0 : 550;
      this.autoplayEnabled =
        this.dataset.autoplay === 'true' &&
        !this.reducedMotion;
      this.autoplayDelayMs =
        Math.min(15, Math.max(5, parseInt(this.dataset.autoplaySpeed, 10) || 8)) * 1000;

      this.prevButton?.addEventListener('click', () => {
        this.pauseAutoplayForInteraction();
        this.step(-1);
      });
      this.nextButton?.addEventListener('click', () => {
        this.pauseAutoplayForInteraction();
        this.step(1);
      });

      this.dots.forEach((dot, dotIndex) => {
        dot.addEventListener('click', () => {
          this.pauseAutoplayForInteraction();
          this.goTo(dotIndex);
        });
      });

      this.track.addEventListener('click', this.handleTrackClick);
      this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
      this.bindDrag();
      this.bindDragCursor();
      this.mediaQuery.addEventListener('change', this.handleResize);
      window.addEventListener('resize', this.handleResize);

      this.prepareTrack();
      this.update(false);
      this.queueLayoutSync();

      this.bindSlideImages();
      this.bindViewportResize();
      this.bindAutoplay();
    }

    bindSlideImages() {
      const images = this.querySelectorAll('.pastael-community__slide-image, .pastael-community-pdp__image');
      images.forEach((image) => {
        if (image.complete) return;
        image.addEventListener('load', () => this.recenterActiveSlide(), { once: true });
      });
    }

    bindViewportResize() {
      if (!this.viewport || typeof ResizeObserver === 'undefined') return;

      this.viewportResizeObserver = new ResizeObserver(() => {
        if (this.isAnimating || this.isLooping) return;

        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = window.requestAnimationFrame(() => {
          this.cacheSlideMetrics();
          this.recenterActiveSlide();
        });
      });

      this.viewportResizeObserver.observe(this.viewport);
    }

    bindAutoplay() {
      if (!this.autoplayEnabled || this.slideCount <= 1) return;

      this.onAutoplayPointerEnter = () => {
        this.autoplayPaused = true;
        this.stopAutoplay();
      };

      this.onAutoplayPointerLeave = () => {
        this.autoplayPaused = false;
        this.scheduleAutoplay();
      };

      this.onAutoplayFocusIn = (event) => {
        if (!this.contains(event.target)) return;
        this.autoplayPaused = true;
        this.stopAutoplay();
      };

      this.onAutoplayFocusOut = (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.autoplayPaused = false;
        this.scheduleAutoplay();
      };

      this.onVisibilityChange = () => {
        if (document.hidden) {
          this.stopAutoplay();
          return;
        }

        if (!this.autoplayPaused) {
          this.scheduleAutoplay();
        }
      };

      this.addEventListener('mouseenter', this.onAutoplayPointerEnter);
      this.addEventListener('mouseleave', this.onAutoplayPointerLeave);
      this.addEventListener('focusin', this.onAutoplayFocusIn);
      this.addEventListener('focusout', this.onAutoplayFocusOut);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    pauseAutoplayForInteraction() {
      this.autoplayPaused = true;
      this.stopAutoplay();
      window.setTimeout(() => {
        this.autoplayPaused = false;
        this.scheduleAutoplay();
      }, this.autoplayDelayMs);
    }

    stopAutoplay() {
      window.clearTimeout(this.autoplayTimeout);
      this.autoplayTimeout = null;
    }

    scheduleAutoplay() {
      this.stopAutoplay();

      if (!this.autoplayEnabled || this.slideCount <= 1 || this.autoplayPaused || document.hidden) {
        return;
      }

      this.autoplayTimeout = window.setTimeout(() => {
        if (this.isAnimating || this.isLooping || this.autoplayPaused) {
          this.scheduleAutoplay();
          return;
        }

        this.step(1);
      }, this.autoplayDelayMs);
    }

    onSlideMotionComplete() {
      this.isAnimating = false;
      this.scheduleAutoplay();
    }

    refreshSlideItems() {
      this.slideItems = Array.from(this.track.querySelectorAll('[data-slider-slide]'));
    }

    cacheSlideMetrics() {
      if (!this.track || !this.originalSlides[0]) return;

      const trackStyles = getComputedStyle(this.track);
      this.trackGap = parseFloat(trackStyles.gap) || 0;
      this.trackPaddingStart = parseFloat(trackStyles.paddingInlineStart || trackStyles.paddingLeft) || 0;

      if (this.inactiveSlideWidth && this.activeSlideWidth) return;

      const slide = this.originalSlides[0];
      const previousTransition = slide.style.transition;
      const wasActive = slide.classList.contains('is-active');

      slide.style.transition = 'none';
      slide.classList.remove('is-active');
      this.inactiveSlideWidth = slide.getBoundingClientRect().width;

      slide.classList.add('is-active');
      this.activeSlideWidth = slide.getBoundingClientRect().width;

      if (!wasActive) slide.classList.remove('is-active');
      slide.style.transition = previousTransition;

      if (!this.inactiveSlideWidth) {
        this.inactiveSlideWidth = slide.offsetWidth;
      }

      if (!this.activeSlideWidth) {
        this.activeSlideWidth = this.inactiveSlideWidth;
      }
    }

    getSlideWidth(physicalIndex, activePhysical) {
      return physicalIndex === activePhysical ? this.activeSlideWidth : this.inactiveSlideWidth;
    }

    getSlideCenterByIndex(physicalIndex, activePhysical = this.getPhysicalIndex()) {
      let center = this.trackPaddingStart;

      for (let index = 0; index < physicalIndex; index += 1) {
        center += this.getSlideWidth(index, activePhysical) + this.trackGap;
      }

      return center + this.getSlideWidth(physicalIndex, activePhysical) / 2;
    }

    getSlideCenterInTrack(slide, activePhysical = this.getPhysicalIndex()) {
      const physicalIndex = this.slideItems.indexOf(slide);
      if (physicalIndex === -1 || !this.inactiveSlideWidth) {
        return slide.offsetLeft - this.track.offsetLeft + slide.offsetWidth / 2;
      }

      return this.getSlideCenterByIndex(physicalIndex, activePhysical);
    }

    getCenteredScrollLeft(slide, activePhysical = this.getPhysicalIndex()) {
      if (!slide || !this.viewport) return 0;

      const slideCenter = this.getSlideCenterInTrack(slide, activePhysical);
      const targetLeft = slideCenter - this.viewport.clientWidth / 2;
      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);

      return Math.max(0, Math.min(targetLeft, maxScroll));
    }

    queueLayoutSync() {
      window.cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.cacheSlideMetrics();
          this.recenterActiveSlide();
          this.classList.add('is-ready');
          this.scheduleAutoplay();
        });
      });
    }

    recenterActiveSlide() {
      if (this.isDesktop) {
        this.snapToPhysical(this.getPhysicalIndex());
        return;
      }

      this.syncControls();
      this.scrollToActive(false);
    }

    disconnectedCallback() {
      this.track?.removeEventListener('click', this.handleTrackClick);
      this.viewport?.removeEventListener('pointerdown', this.onDragPointerDown);
      this.clearDragListeners();
      this.viewport?.removeEventListener('scroll', this.handleScroll);
      this.mediaQuery?.removeEventListener('change', this.handleResize);
      window.removeEventListener('resize', this.handleResize);
      this.viewportResizeObserver?.disconnect();
      this.stopAutoplay();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.removeEventListener('mouseenter', this.onAutoplayPointerEnter);
      this.removeEventListener('mouseleave', this.onAutoplayPointerLeave);
      this.removeEventListener('focusin', this.onAutoplayFocusIn);
      this.removeEventListener('focusout', this.onAutoplayFocusOut);
      this.removeEventListener('mousemove', this.onDragCursorMove);
      this.removeEventListener('mouseleave', this.hideDragCursor);
      this.finePointerMedia?.removeEventListener('change', this.hideDragCursor);
      if (this.dragCursorFrame) cancelAnimationFrame(this.dragCursorFrame);
      window.cancelAnimationFrame(this.layoutFrame);
      window.cancelAnimationFrame(this.resizeFrame);
      window.clearTimeout(this.resizeTimeout);
      this.clearLoopFallback();
      this.clearTrackMotionFallback();
      this.removeClones();
    }

    get isDesktop() {
      return this.mediaQuery.matches;
    }

    get slideCount() {
      return this.originalSlides.length;
    }

    get slides() {
      return this.slideItems;
    }

    getPhysicalIndex(logicalIndex = this.index) {
      return this.prependCount + logicalIndex;
    }

    setTrackTransition(enabled) {
      if (!this.track) return;

      this.track.style.transition = enabled && this.transitionMs
        ? `transform ${this.transitionMs}ms ${this.easing}`
        : 'none';
    }

    setSlideTransitions(enabled) {
      const value = enabled ? '' : 'none';
      this.slideItems.forEach((slide) => {
        slide.style.transition = value;
      });
    }

    snapToPhysical(physicalIndex) {
      this.setTrackTransition(false);
      this.setSlideTransitions(false);
      this.syncControls(physicalIndex);
      this.centerPhysicalSlide(physicalIndex);
      void this.track.offsetWidth;

      requestAnimationFrame(() => {
        this.setTrackTransition(true);
        this.setSlideTransitions(true);
      });
    }

    step(delta) {
      if (this.slideCount <= 1 || this.isLooping || this.isAnimating) return;

      const previousIndex = this.index;
      this.index = (this.index + delta + this.slideCount) % this.slideCount;

      let targetPhysical = this.getPhysicalIndex();
      let normalizeAfter = false;

      if (delta === 1 && previousIndex === this.slideCount - 1) {
        targetPhysical = this.prependCount + this.slideCount;
        normalizeAfter = true;
      } else if (delta === -1 && previousIndex === 0) {
        targetPhysical = this.prependCount - 1;
        normalizeAfter = true;
      }

      this.moveToPhysical(targetPhysical, true, normalizeAfter);
    }

    goTo(index) {
      if (this.slideCount <= 1 || this.isLooping || this.isAnimating) return;

      const nextIndex = ((index % this.slideCount) + this.slideCount) % this.slideCount;
      if (nextIndex === this.index) return;

      const previousIndex = this.index;
      this.index = nextIndex;

      const wrapsForward = previousIndex === this.slideCount - 1 && nextIndex === 0;
      const wrapsBackward = previousIndex === 0 && nextIndex === this.slideCount - 1;

      if (wrapsForward) {
        this.moveToPhysical(this.prependCount + this.slideCount, true, true);
        return;
      }

      if (wrapsBackward) {
        this.moveToPhysical(this.prependCount - 1, true, true);
        return;
      }

      this.moveToPhysical(this.getPhysicalIndex(), true, false);
    }

    bindDrag() {
      this.onDragPointerDown = (event) => this.handleDragPointerDown(event);
      this.onDragPointerMove = (event) => this.handleDragPointerMove(event);
      this.onDragPointerUp = (event) => this.handleDragPointerUp(event);
      this.viewport.addEventListener('pointerdown', this.onDragPointerDown);
    }

    bindDragCursor() {
      this.dragCursor = this.querySelector('[data-drag-cursor]');
      if (!this.dragCursor) return;

      this.finePointerMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.dragCursorTarget = { x: 0, y: 0 };
      this.dragCursorFrame = null;
      this.onDragCursorMove = this.handleDragCursorMove.bind(this);
      this.hideDragCursor = this.hideDragCursor.bind(this);

      this.addEventListener('mousemove', this.onDragCursorMove);
      this.addEventListener('mouseleave', this.hideDragCursor);
      this.finePointerMedia.addEventListener('change', this.hideDragCursor);
    }

    hideDragCursor() {
      if (this.dragCursorFrame) {
        cancelAnimationFrame(this.dragCursorFrame);
        this.dragCursorFrame = null;
      }

      this.classList.remove('is-drag-cursor-visible');
      if (this.dragCursor) {
        this.dragCursor.hidden = true;
        this.dragCursor.style.removeProperty('left');
        this.dragCursor.style.removeProperty('top');
      }
    }

    applyDragCursorPosition() {
      this.dragCursorFrame = null;
      if (!this.dragCursor || this.dragCursor.hidden) return;
      this.dragCursor.style.left = `${this.dragCursorTarget.x}px`;
      this.dragCursor.style.top = `${this.dragCursorTarget.y}px`;
    }

    handleDragCursorMove(event) {
      if (!this.dragCursor || !this.finePointerMedia?.matches || this.slideCount <= 1) {
        this.hideDragCursor();
        return;
      }

      if (event.target.closest('a, button, input, select, textarea, label')) {
        this.hideDragCursor();
        return;
      }

      const bounds = this.getBoundingClientRect();
      if (
        event.clientX < bounds.left ||
        event.clientX > bounds.right ||
        event.clientY < bounds.top ||
        event.clientY > bounds.bottom
      ) {
        this.hideDragCursor();
        return;
      }

      this.dragCursorTarget.x = event.clientX - bounds.left;
      this.dragCursorTarget.y = event.clientY - bounds.top;
      this.classList.add('is-drag-cursor-visible');
      this.dragCursor.hidden = false;

      if (this.dragCursorFrame) return;
      this.dragCursorFrame = requestAnimationFrame(() => this.applyDragCursorPosition());
    }

    clearDragListeners() {
      window.removeEventListener('pointermove', this.onDragPointerMove);
      window.removeEventListener('pointerup', this.onDragPointerUp);
      window.removeEventListener('pointercancel', this.onDragPointerUp);
    }

    readTrackTranslate() {
      if (!this.track) return 0;
      const transform = getComputedStyle(this.track).transform;
      if (!transform || transform === 'none') return 0;
      return new DOMMatrix(transform).m41;
    }

    getClosestPhysicalIndexFromTranslate(translate) {
      const viewportCenter = this.viewport.clientWidth / 2;
      const activePhysical = this.getPhysicalIndex();
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.slideItems.forEach((_slide, slideIndex) => {
        const slideCenter = this.getSlideCenterByIndex(slideIndex, activePhysical);
        const distance = Math.abs(slideCenter + translate - viewportCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = slideIndex;
        }
      });

      return closestIndex;
    }

    handleDragPointerDown(event) {
      if (!this.isDesktop && event.pointerType === 'touch') return;
      if (event.button !== 0) return;
      if (event.target.closest('a, button, input, select, textarea, label')) return;
      if (this.slideCount <= 1) return;

      this.dragPointerId = event.pointerId;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.isPointerDragging = false;
      this.dragStartScroll = this.viewport.scrollLeft;
      this.dragStartTranslate = this.readTrackTranslate();

      window.addEventListener('pointermove', this.onDragPointerMove);
      window.addEventListener('pointerup', this.onDragPointerUp);
      window.addEventListener('pointercancel', this.onDragPointerUp);
    }

    handleDragPointerMove(event) {
      if (event.pointerId !== this.dragPointerId) return;

      const deltaX = event.clientX - this.dragStartX;
      const deltaY = event.clientY - this.dragStartY;

      if (!this.isPointerDragging) {
        if (Math.abs(deltaX) < 8) return;
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          this.clearDragListeners();
          return;
        }

        this.isPointerDragging = true;
        this.suppressClick = true;
        this.classList.add('is-dragging');
        this.pauseAutoplayForInteraction();
        this.isAnimating = false;
        this.isLooping = false;
        this.clearTrackMotionFallback();
        this.clearLoopFallback();

        if (this.isDesktop) {
          this.setTrackTransition(false);
        } else {
          this.viewport.style.scrollSnapType = 'none';
        }
      }

      if (this.isDesktop) {
        const nextTranslate = Math.round(this.dragStartTranslate + deltaX);
        this.track.style.transform = `translate3d(${nextTranslate}px, 0, 0)`;
      } else {
        this.viewport.scrollLeft = this.dragStartScroll - deltaX;
      }

      event.preventDefault();
    }

    handleDragPointerUp(event) {
      if (event.pointerId !== this.dragPointerId) return;

      const wasDragging = this.isPointerDragging;
      this.clearDragListeners();
      this.classList.remove('is-dragging');
      this.isPointerDragging = false;
      this.dragPointerId = null;

      if (!wasDragging) {
        this.suppressClick = false;
        return;
      }

      if (this.isDesktop) {
        this.finishDesktopDrag();
        return;
      }

      this.viewport.style.scrollSnapType = '';
      const closestIndex = this.getClosestPhysicalIndex();
      const slide = this.slides[closestIndex];
      const logicalIndex = slide ? this.getLogicalIndexFromSlide(slide) : -1;
      if (logicalIndex === -1) return;

      this.index = logicalIndex;
      const isClone =
        closestIndex < this.prependCount || closestIndex >= this.prependCount + this.slideCount;
      this.moveMobileToPhysical(closestIndex, true, isClone);
    }

    finishDesktopDrag() {
      const physicalIndex = this.getClosestPhysicalIndexFromTranslate(this.readTrackTranslate());
      const slide = this.slides[physicalIndex];
      const logicalIndex = slide ? this.getLogicalIndexFromSlide(slide) : -1;
      if (logicalIndex === -1) return;

      this.index = logicalIndex;
      const isClone =
        physicalIndex < this.prependCount || physicalIndex >= this.prependCount + this.slideCount;
      this.moveToPhysical(physicalIndex, true, isClone);
    }

    handleTrackClick(event) {
      if (this.suppressClick) {
        this.suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (event.target.closest('a, button')) return;

      const slide = event.target.closest('[data-slider-slide]');
      if (!slide) return;

      const logicalIndex = this.getLogicalIndexFromSlide(slide);
      if (logicalIndex === -1 || logicalIndex === this.index) return;

      if (slide.dataset.sliderClone === 'true') {
        const prevIndex = (this.index - 1 + this.slideCount) % this.slideCount;
        const nextIndex = (this.index + 1) % this.slideCount;
        if (logicalIndex !== prevIndex && logicalIndex !== nextIndex) return;
      }

      this.pauseAutoplayForInteraction();
      this.goTo(logicalIndex);
    }

    getLogicalIndexFromSlide(slide) {
      if (slide.dataset.sliderClone === 'true') {
        return parseInt(slide.dataset.sliderSourceIndex, 10);
      }

      return this.originalSlides.indexOf(slide);
    }

    handleScroll() {
      if (this.isDesktop || this.isScrollingProgrammatically || this.isLooping || this.isAnimating) {
        return;
      }

      window.clearTimeout(this.scrollTimeout);
      this.scrollTimeout = window.setTimeout(() => {
        this.syncMobileScrollState();
      }, 80);
    }

    getClosestPhysicalIndex() {
      const viewportCenter = this.viewport.scrollLeft + this.viewport.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      const activePhysical = this.getPhysicalIndex();

      this.slideItems.forEach((_slide, slideIndex) => {
        const slideCenter = this.getSlideCenterByIndex(slideIndex, activePhysical);
        const distance = Math.abs(slideCenter - viewportCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = slideIndex;
        }
      });

      return closestIndex;
    }

    syncMobileScrollState() {
      if (this.isDesktop || this.isScrollingProgrammatically || this.isLooping) return;

      const closestIndex = this.getClosestPhysicalIndex();
      const slide = this.slides[closestIndex];
      const logicalIndex = this.getLogicalIndexFromSlide(slide);
      if (logicalIndex === -1) return;

      const isCloneZone =
        closestIndex < this.prependCount || closestIndex >= this.prependCount + this.slideCount;

      this.index = logicalIndex;
      this.syncControls(closestIndex);

      if (isCloneZone) {
        this.normalizeMobileScroll();
      }
    }

    handleResize() {
      window.clearTimeout(this.resizeTimeout);
      this.resizeTimeout = window.setTimeout(() => {
        this.prepareTrack();
        this.update(false);
      }, 80);
    }

    prepareTrack() {
      const nextMode = this.isDesktop ? 'desktop' : 'mobile';
      if (this.metricsMode !== nextMode) {
        this.inactiveSlideWidth = 0;
        this.activeSlideWidth = 0;
        this.metricsMode = nextMode;
      }

      if (this.slideCount <= 1) {
        this.removeClones();
        this.refreshSlideItems();
        this.cacheSlideMetrics();
        return;
      }

      if (this.isDesktop) {
        this.cacheSlideMetrics();
        this.buildDesktopClones();
        this.refreshSlideItems();
        return;
      }

      this.buildMobileClones();
      this.refreshSlideItems();
      this.cacheSlideMetrics();
    }

    buildDesktopClones() {
      const gap = this.trackGap;
      const targetWidth = this.viewport.clientWidth / 2;
      const slideWidth = this.inactiveSlideWidth;
      const stride = slideWidth > 0 ? slideWidth + gap : 0;

      if (!stride) {
        this.buildEdgeClones();
        return;
      }

      let prependNeeded = 0;
      let leftWidth = 0;

      while (leftWidth < targetWidth && prependNeeded < this.slideCount - 1) {
        prependNeeded += 1;
        leftWidth += stride;
      }

      let appendNeeded = 0;
      let rightWidth = 0;

      while (rightWidth < targetWidth && appendNeeded < this.slideCount - 1) {
        appendNeeded += 1;
        rightWidth += stride;
      }

      if (
        this.trackMode === 'desktop' &&
        this.prependCount === prependNeeded &&
        this.appendCount === appendNeeded
      ) {
        return;
      }

      this.removeClones();

      const anchor = this.originalSlides[0];
      const prepended = [];
      let prependSource = this.slideCount - 1;

      for (let count = 0; count < prependNeeded; count += 1) {
        prepended.unshift(this.cloneSlide(this.originalSlides[prependSource], prependSource));
        prependSource -= 1;
        if (prependSource < 0) break;
      }

      this.prependCount = prepended.length;
      this.appendCount = appendNeeded;
      this.trackMode = 'desktop';

      prepended.forEach((clone) => {
        this.track.insertBefore(clone, anchor);
      });

      for (let count = 0; count < appendNeeded; count += 1) {
        const appendSource = count % this.slideCount;
        this.track.appendChild(this.cloneSlide(this.originalSlides[appendSource], appendSource));
      }
    }

    buildMobileClones() {
      this.buildEdgeClones();
    }

    buildEdgeClones() {
      if (this.trackMode === 'mobile' && this.prependCount === 1 && this.appendCount === 1) {
        return;
      }

      this.removeClones();

      this.track.insertBefore(
        this.cloneSlide(this.originalSlides[this.slideCount - 1], this.slideCount - 1),
        this.track.firstChild
      );

      this.prependCount = 1;
      this.appendCount = 1;
      this.trackMode = 'mobile';

      this.track.appendChild(this.cloneSlide(this.originalSlides[0], 0));
    }

    cloneSlide(slide, sourceIndex) {
      const clone = slide.cloneNode(true);
      clone.classList.remove('is-active');
      clone.dataset.sliderClone = 'true';
      clone.dataset.sliderSourceIndex = String(sourceIndex);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');

      clone.querySelectorAll('img').forEach((image) => {
        image.removeAttribute('loading');
      });

      return clone;
    }

    removeClones() {
      this.track.querySelectorAll('[data-slider-clone="true"]').forEach((clone) => clone.remove());
      this.prependCount = 0;
      this.appendCount = 0;
      this.trackMode = '';
      this.track.style.transform = '';
      this.isLooping = false;
      this.clearLoopFallback();
      this.refreshSlideItems();
    }

    syncControls(physicalIndex = this.getPhysicalIndex()) {
      this.setPhysicalActive(physicalIndex);
      this.syncDots();

      const disableNav = this.slideCount <= 1;
      if (this.prevButton) {
        this.prevButton.disabled = disableNav;
      }

      if (this.nextButton) {
        this.nextButton.disabled = disableNav;
      }
    }

    update(animate) {
      this.moveToPhysical(this.getPhysicalIndex(), animate, false);
    }

    moveToPhysical(physicalIndex, animate, normalizeAfter) {
      if (!this.isDesktop) {
        this.moveMobileToPhysical(physicalIndex, animate, normalizeAfter);
        return;
      }

      this.syncDots();
      this.stopAutoplay();

      if (!animate || this.reducedMotion) {
        this.clearLoopFallback();
        this.isLooping = false;
        this.isAnimating = false;
        this.snapToPhysical(physicalIndex);
        return;
      }

      this.isAnimating = true;
      this.clearTrackMotionFallback();

      if (normalizeAfter) {
        this.isLooping = true;
        this.normalizeTargetPhysical = this.getPhysicalIndex();
        this.queueNormalizeFallback();
      } else {
        this.isLooping = false;
        this.clearLoopFallback();
      }

      this.syncControls(physicalIndex);
      this.animateTrackToPhysical(physicalIndex, () => {
        if (normalizeAfter) {
          this.finishNormalize();
          return;
        }

        this.onSlideMotionComplete();
      });
    }

    moveMobileToPhysical(physicalIndex, animate, normalizeAfter) {
      const slide = this.slides[physicalIndex];
      if (!slide) return;

      this.syncControls(physicalIndex);
      this.stopAutoplay();

      if (normalizeAfter) {
        this.isLooping = true;
        this.normalizeTargetPhysical = this.getPhysicalIndex();
      } else {
        this.isLooping = false;
      }

      this.scrollToSlide(slide, animate, physicalIndex, () => {
        if (normalizeAfter) {
          this.finishMobileNormalize();
          return;
        }

        this.onSlideMotionComplete();
      });
    }

    animateTrackToPhysical(physicalIndex, onComplete) {
      if (!this.viewport) {
        this.isAnimating = false;
        onComplete?.();
        return;
      }

      const viewportCenter = this.viewport.clientWidth / 2;
      const slideCenter = this.getSlideCenterByIndex(physicalIndex, physicalIndex);
      const targetTranslate = Math.round(viewportCenter - slideCenter);

      const complete = () => {
        this.track.removeEventListener('transitionend', onTransitionEnd);
        this.clearTrackMotionFallback();
        onComplete?.();
      };

      const onTransitionEnd = (event) => {
        if (event.target !== this.track || event.propertyName !== 'transform') return;
        complete();
      };

      this.setTrackTransition(true);
      this.track.style.transform = `translate3d(${targetTranslate}px, 0, 0)`;
      this.track.addEventListener('transitionend', onTransitionEnd);

      this.trackMotionFallback = window.setTimeout(() => {
        if (!this.isAnimating) return;
        complete();
      }, this.transitionMs + 80);
    }

    clearTrackMotionFallback() {
      window.clearTimeout(this.trackMotionFallback);
      this.trackMotionFallback = null;
    }

    finishNormalize() {
      if (!this.isLooping) return;

      this.clearLoopFallback();

      const targetPhysical = this.normalizeTargetPhysical ?? this.getPhysicalIndex();

      this.setTrackTransition(false);
      this.setSlideTransitions(false);
      this.syncControls(targetPhysical);
      this.centerPhysicalSlide(targetPhysical);
      void this.track.offsetWidth;

      requestAnimationFrame(() => {
        this.setTrackTransition(true);
        this.setSlideTransitions(true);
        this.isLooping = false;
        this.normalizeTargetPhysical = null;
        this.onSlideMotionComplete();
      });
    }

    finishMobileNormalize() {
      const targetPhysical = this.normalizeTargetPhysical ?? this.getPhysicalIndex();
      const slide = this.slides[targetPhysical];
      if (!slide) {
        this.isLooping = false;
        this.onSlideMotionComplete();
        return;
      }

      this.viewport.style.scrollSnapType = 'none';
      this.isScrollingProgrammatically = true;
      this.viewport.scrollLeft = this.getCenteredScrollLeft(slide, targetPhysical);
      this.syncControls(targetPhysical);

      requestAnimationFrame(() => {
        this.viewport.style.scrollSnapType = '';
        this.isScrollingProgrammatically = false;
        this.isLooping = false;
        this.normalizeTargetPhysical = null;
        this.onSlideMotionComplete();
      });
    }

    queueNormalizeFallback() {
      this.clearLoopFallback();
      this.loopTimeout = window.setTimeout(() => {
        if (this.isLooping) this.finishNormalize();
      }, this.transitionMs + 120);
    }

    clearLoopFallback() {
      window.clearTimeout(this.loopTimeout);
      this.loopTimeout = null;
    }

    syncDots() {
      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === this.index;
        dot.classList.toggle('is-active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    setPhysicalActive(physicalIndex) {
      this.slideItems.forEach((slide, slideIndex) => {
        const isActive = slideIndex === physicalIndex;
        slide.classList.toggle('is-active', isActive);

        if (slide.dataset.sliderClone === 'true') {
          slide.setAttribute('aria-hidden', 'true');
          return;
        }

        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
    }

    centerPhysicalSlide(physicalIndex) {
      if (!this.viewport) return;

      const viewportCenter = this.viewport.clientWidth / 2;
      const slideCenter = this.getSlideCenterByIndex(physicalIndex, physicalIndex);
      const translateX = Math.round(viewportCenter - slideCenter);

      this.track.style.transform = `translate3d(${translateX}px, 0, 0)`;
    }

    normalizeMobileScroll() {
      const physicalIndex = this.getPhysicalIndex();
      const slide = this.slides[physicalIndex] || this.originalSlides[this.index];
      if (!slide || !this.viewport) return;

      this.isScrollingProgrammatically = true;
      this.viewport.style.scrollSnapType = 'none';
      this.viewport.scrollLeft = this.getCenteredScrollLeft(slide, physicalIndex);
      this.syncControls(physicalIndex);

      window.setTimeout(() => {
        this.viewport.style.scrollSnapType = '';
        this.isScrollingProgrammatically = false;
      }, 50);
    }

    scrollToActive(animate) {
      const physicalIndex = this.getPhysicalIndex();
      const slide = this.slides[physicalIndex] || this.originalSlides[this.index];
      if (!slide) return;

      this.scrollToSlide(slide, animate, physicalIndex, () => {
        if (animate) this.onSlideMotionComplete();
      });
    }

    scrollToSlide(slide, animate, activePhysical, onComplete) {
      if (!slide || !this.viewport) {
        onComplete?.();
        return;
      }

      const targetLeft = this.getCenteredScrollLeft(slide, activePhysical);

      this.stopAutoplay();
      this.isScrollingProgrammatically = true;

      if (animate) {
        this.isAnimating = true;
      }

      const complete = () => {
        this.viewport.removeEventListener('scrollend', onScrollEnd);
        window.clearTimeout(this.mobileScrollTimeout);
        this.isScrollingProgrammatically = false;
        onComplete?.();
      };

      const onScrollEnd = () => complete();

      if (!animate || this.reducedMotion) {
        this.viewport.scrollLeft = targetLeft;
        complete();
        return;
      }

      this.viewport.addEventListener('scrollend', onScrollEnd, { once: true });
      this.viewport.scrollTo({
        left: targetLeft,
        behavior: 'smooth',
      });

      this.mobileScrollTimeout = window.setTimeout(complete, this.transitionMs + 80);
    }
  }

  customElements.define('pastael-community-slider', PastaelCommunitySlider);
}
